import { extractLoneMediaUrl } from "@/components/ChatEmojiGifPicker";
import type { ChatAttachment, ChatColleague, ChatConversation, ChatMessage } from "@/lib/chatApi";

export type ChatMobilePane = "list" | "thread";

export function isMissedCallBody(body: string): "audio" | "video" | null {
  const t = (body || "").trim();
  if (/appel audio manqué/i.test(t)) return "audio";
  if (/appel vidéo manqué|appel manqué/i.test(t)) return "video";
  return null;
}

/** Invité externe entré en salle d’attente (fil hôte). */
export function isGuestLobbyBody(body: string): boolean {
  return /est dans la salle d[’']attente/i.test((body || "").trim());
}

/** Rappel visio planifiée (message système + push membres Suite). */
export function isMeetingReminderBody(body: string): boolean {
  return /^⏰\s*Visio dans/i.test((body || "").trim());
}

/** Trace d’appel dans le fil (manqué / annulé / refusé) → bouton Rappeler. */
export function isCallLogBody(body: string): boolean {
  const t = (body || "").trim();
  if (isMissedCallBody(t)) return true;
  return /essayé de vous joindre|appel refusé|visio annulée|appel annulé/i.test(t);
}

export function isImageAttachment(a: ChatAttachment): boolean {
  const mime = (a.mime || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(gif|png|jpe?g|webp|svg)(\?|$)/i.test(a.url) || /\.(gif|png|jpe?g|webp)$/i.test(a.name);
}

export function isAudioAttachment(a: ChatAttachment): boolean {
  const mime = (a.mime || "").toLowerCase();
  const name = (a.name || "").toLowerCase();
  if (name.startsWith("vocal-") || name.includes("/vocal-") || /(?:^|_)vocal-/.test(name)) return true;
  if (mime.startsWith("audio/")) return true;
  if (mime === "application/ogg") return true;
  if (mime.startsWith("video/")) return false;
  return /\.(ogg|oga|m4a|mp3|wav|aac)(\?|$)/i.test(a.url) || /\.(ogg|oga|m4a|mp3|wav|aac)$/i.test(name);
}

export function isVideoAttachment(a: ChatAttachment): boolean {
  if (isAudioAttachment(a)) return false;
  const mime = (a.mime || "").toLowerCase();
  const name = (a.name || "").toLowerCase();
  if (mime.startsWith("video/")) return true;
  return /\.(mp4|mov|m4v|3gp|webm)(\?|$)/i.test(a.url) || /\.(mp4|mov|m4v|3gp|webm)$/i.test(name);
}

export function isVoiceOnlyMessage(m: ChatMessage): boolean {
  const atts = m.attachments ?? [];
  if (atts.length === 0 || !atts.every(isAudioAttachment)) return false;
  const body = (m.body || "").trim();
  if (!body) return true;
  if (/^\[[^\]]+\]$/.test(body)) return true;
  return false;
}

/** Message = uniquement photo(s) (pas de vrai texte) → bulle média sans cadre. */
export function isImageOnlyMessage(m: ChatMessage): boolean {
  const atts = m.attachments ?? [];
  if (atts.length === 0 || !atts.every(isImageAttachment)) return false;
  const body = (m.body || "").trim();
  if (!body) return true;
  if (/^\[[^\]]+\]$/.test(body)) return true;
  return false;
}

/** Message = uniquement vidéo(s) (pas de vrai texte) → bulle média sans cadre. */
export function isVideoOnlyMessage(m: ChatMessage): boolean {
  const atts = m.attachments ?? [];
  if (atts.length === 0 || !atts.every(isVideoAttachment)) return false;
  const body = (m.body || "").trim();
  if (!body) return true;
  if (/^\[[^\]]+\]$/.test(body)) return true;
  return false;
}

export function memberLabel(m: ChatColleague | undefined, fallback: string): string {
  if (!m) return fallback;
  const dn = m.display_name?.trim();
  return dn || m.username || fallback;
}

export function conversationTitle(c: ChatConversation, selfId: number | null): string {
  if (c.title && c.title.trim()) return c.title;
  if (c.type === "group") return "Groupe";
  const others =
    c.members?.filter((m) => m.id !== selfId).map((m) => memberLabel(m, m.username)) ??
    c.member_ids.filter((id) => id !== selfId).map((id) => `#${id}`);
  return others.join(", ") || "Discussion";
}

/** Aperçu liste : jamais d’URL média brute (élargit la sidebar). */
export function conversationListPreview(c: ChatConversation): string {
  const last = c.last_message;
  const body = last?.body?.trim() ?? "";
  const atts = last?.attachments ?? [];
  if ((!body || /^\[[^\]]+\]$/.test(body)) && atts.length > 0) {
    if (atts.every(isAudioAttachment)) return "Message vocal";
    if (atts.every(isImageAttachment)) return "Image";
    if (atts.every(isVideoAttachment)) return "Vidéo";
    return "Fichier";
  }
  if (!body) return c.type === "group" ? "Groupe" : "Message privé";
  const media = extractLoneMediaUrl(body);
  if (media) {
    if (/\.gif(\?|$)/i.test(media) || /giphy\.com|tenor\.com|media\.tenor/i.test(media)) {
      return "GIF";
    }
    return "Image";
  }
  return body;
}

export function peerAvatarUrl(c: ChatConversation, selfId: number | null): string | null {
  if (c.type === "group") return c.avatar_url ?? null;
  const peer = c.members?.find((m) => m.id !== selfId);
  return peer?.avatar_url ?? null;
}

/** Intersection des client_ids (sélection groupe), bornée aux clients de l’utilisateur. */
export function intersectColleagueClients(
  selected: ChatColleague[],
  myClientIds?: number[]
): number[] | null {
  if (selected.length === 0) {
    return myClientIds && myClientIds.length > 0 ? [...myClientIds] : null;
  }
  let acc: number[] | null = null;
  for (const c of selected) {
    const ids = (c.client_ids ?? []).filter((id) => id > 0);
    if (ids.length === 0) return [];
    acc = acc === null ? [...ids] : acc.filter((id) => ids.includes(id));
  }
  if (acc === null) return [];
  if (myClientIds && myClientIds.length > 0) {
    return acc.filter((id) => myClientIds.includes(id));
  }
  return acc;
}

export function colleagueSharesClients(c: ChatColleague, required: number[] | null): boolean {
  if (required === null) return true;
  if (required.length === 0) return false;
  const ids = c.client_ids ?? [];
  return required.some((id) => ids.includes(id));
}

export function formatChatTime(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function chatMessageDayKey(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

function capitalizeFr(s: string): string {
  const t = s.trim();
  if (!t) return t;
  return t.charAt(0).toLocaleUpperCase("fr-FR") + t.slice(1);
}

/** Bandeau de jour dans le fil (Aujourd’hui / Hier / Lundi / 8 septembre). */
export function formatChatDaySeparator(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayDiff = Math.round((startToday - startThat) / 86_400_000);
    if (dayDiff === 0) return "Aujourd’hui";
    if (dayDiff === 1) return "Hier";
    if (dayDiff > 1 && dayDiff < 7) {
      return capitalizeFr(d.toLocaleDateString("fr-FR", { weekday: "long" }));
    }
    const sameYear = d.getFullYear() === now.getFullYear();
    return capitalizeFr(
      d.toLocaleDateString(
        "fr-FR",
        sameYear
          ? { weekday: "long", day: "numeric", month: "long" }
          : { weekday: "long", day: "numeric", month: "long", year: "numeric" }
      )
    );
  } catch {
    return "";
  }
}

export function formatChatThreadDayLabel(
  prevIso?: string | null,
  iso?: string | null
): string {
  const key = chatMessageDayKey(iso);
  if (!key) return "";
  if (prevIso && chatMessageDayKey(prevIso) === key) return "";
  return formatChatDaySeparator(iso);
}

/** Liste des conversations (sidebar) — comme WhatsApp / Messenger. */
export function formatChatListWhen(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayDiff = Math.round((startToday - startThat) / 86_400_000);
    if (dayDiff === 0) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    if (dayDiff === 1) return "Hier";
    if (dayDiff > 1 && dayDiff < 7) {
      return capitalizeFr(d.toLocaleDateString("fr-FR", { weekday: "long" }));
    }
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString(
      "fr-FR",
      sameYear
        ? { day: "numeric", month: "short" }
        : { day: "numeric", month: "short", year: "numeric" }
    );
  } catch {
    return "";
  }
}

/** Dernière connexion (unix secondes) → « En ligne aujourd’hui à 09:17 ». */
export function formatChatLastSeen(unix?: number | null): string | null {
  if (unix == null || unix < 1) return null;
  const d = new Date(unix * 1000);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startToday - startThat) / 86_400_000);
  if (dayDiff === 0) return `En ligne aujourd’hui à ${time}`;
  if (dayDiff === 1) return `En ligne hier à ${time}`;
  if (dayDiff > 1 && dayDiff < 7) {
    const day = d.toLocaleDateString("fr-FR", { weekday: "long" });
    return `En ligne ${day} à ${time}`;
  }
  const date = d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  return `En ligne le ${date} à ${time}`;
}

export function parseLastSeenMap(raw?: Record<string, unknown> | null): Record<number, number> {
  const out: Record<number, number> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw)) {
    const id = Number(k);
    const unix = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(id) || id < 1 || !Number.isFinite(unix) || unix < 1) continue;
    out[id] = Math.floor(unix);
  }
  return out;
}
